import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { Comment, CommentTarget } from '../types/api';

export const commentsKeys = {
  all: ['comments'] as const,
  forTarget: (projectId: string | null, type: CommentTarget, id: string) =>
    [...commentsKeys.all, projectId, type, id] as const,
};

export function useCommentsForTarget(target: { type: CommentTarget; id: string | null }) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: target.id
      ? commentsKeys.forTarget(projectId, target.type, target.id)
      : ['comments', 'none'],
    enabled: !!target.id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ comments: Comment[] }>('/comments', {
        target_type: target.type,
        target_id: target.id ?? undefined,
      });
      return res.comments;
    },
  });
}

export function usePostComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      target_type: CommentTarget;
      target_id: string;
      body: string;
      parent_comment_id?: string | null;
    }) => api.post<{ comment: Comment }>('/comments', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKeys.all }),
  });
}

export function useEditComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api.patch<{ comment: Comment }>(`/comments/${id}`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKeys.all }),
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/comments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKeys.all }),
  });
}
